import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import Span from "@vfuk/core-span";

import { IconLink, SpinnerLoader, Stack } from "@ucchub/ui";
import { Modal } from "@ucchub/ui";

import type { AccountConfigFormType } from "@components/accountConfiguration/AccountConfig.types";
import type { AccountConfigFormProps } from "@components/accountConfiguration/AccountConfigForm";
import AccountConfigFormModal from "@components/accountConfiguration/AccountConfigFormModal";
import AccountConfigView from "@components/accountConfiguration/AccountConfigView";
import SuccessModal from "@components/accountConfiguration/SuccessModal";
import WarningModal from "@components/accountConfiguration/WarningModal";
import BillingProfilesList from "@components/billingProfiles/BillingProfilesList";
import CustomerError from "@components/customer/CustomerError";
import DestinationsList from "@components/destinations/Destinations";
import useAccountConfig from "@hooks/accountConfig/useAccountConfig";
import useCreateEditAccountConfig from "@hooks/accountConfig/useCreateEditAccountConfig";
import useBillingProfiles from "@hooks/billingProfiles/useBillingProfiles";
import useDestinations from "@hooks/destinations/useDestinations";

type Props = {
  mode: "add" | "view";
  productName?: string;
  customerId?: string;
  productId?: string;
  customerName: string;
};

const AccountConfigurationTab = ({
  mode,
  productName,
  customerId,
  customerName,
  productId,
}: Props) => {
  const [accountConfigModalOpen, setAccountConfigModalOpen] = useState(
    mode === "add"
  );

  const [successModalModalOpen, setSuccessModalModalOpen] = useState(false);
  const [editWarningModalModalOpen, setEditWarningModalModalOpen] =
    useState(false);

  const [billingProfilesModalOpen, setBillingProfilesModalOpen] =
    useState(false);
  const [destinationsModalOpen, setDestinationsModalOpen] = useState(false);

  const navigate = useNavigate();

  const {
    isAccountConfigSubmitting,
    submitCreateAccountConfig,
    submitEditAccountConfig,
    error: formError,
  } = useCreateEditAccountConfig();
  const { t } = useTranslation();

  const { error, fetchAccountConfig, isLoading, organisationInfo } =
    useAccountConfig();

  const {
    billingProfiles,
    fetchBillingProfiles,
    isLoading: isLoadingBillingProfiles,
  } = useBillingProfiles();

  const {
    destinations,
    fetDestinations,
    isLoading: isLoadingDestinations,
  } = useDestinations();

  useEffect(() => {
    if (customerId && !billingProfiles) {
      fetchBillingProfiles(customerId);
    }
  }, [billingProfilesModalOpen, customerId]);

  useEffect(() => {
    if (customerId && !destinations) {
      fetDestinations(customerId);
    }
  }, [destinationsModalOpen, customerId]);

  useEffect(() => {
    if (mode === "view" && customerId) {
      fetchAccountConfig(customerId);
    }
    if (mode === "add" && !successModalModalOpen) {
      setAccountConfigModalOpen(true);
    }
  }, [mode, customerId]);

  const isTomiaProvisioned = organisationInfo?.tomiaProvisioned;

  const onSubmitAccountConfig: AccountConfigFormProps["onSubmit"] = (data) => {
    if (customerId) {
      if (mode === "add") {
        submitCreateAccountConfig({ id: customerId, ...data }).then(() => {
          setSuccessModalModalOpen(true);
          setAccountConfigModalOpen(false);
        });
      } else {
        submitEditAccountConfig(data, customerId).then(() => {
          setSuccessModalModalOpen(true);
          setAccountConfigModalOpen(false);
          fetchAccountConfig(customerId);
        });
      }
    }
  };

  let accountConfigToEdit = undefined;

  if (mode === "view" && organisationInfo) {
    const {
      accountManagerEmail,
      voicePlatformId,
      commercialManagerEmail,
      messagingPlatformId,
    } = organisationInfo;

    accountConfigToEdit = {
      accountManagerEmail,
      voicePlatformId,
      messagingPlatformId,
      commercialManagerEmail,
    } as AccountConfigFormType;
  }

  const submittedSuccussTitle =
    mode === "add"
      ? t("core:accountConfig:submitted_succuss_title")
      : t("core:accountConfig:edit_submitted_succuss_title");

  const customerLink = "/customers/" + customerId + "/view";

  return (
    <>
      {isLoading && (
        <SpinnerLoader
          srText={t("core:accountConfig:loading") || "Loading ..."}
          text={t("core:accountConfig:loading") || "Loading ..."}
        />
      )}
      {error && <CustomerError error={error} />}
      {mode === "add" && <Stack height="500px" />}
      {mode === "view" && organisationInfo && !isLoading && (
        <AccountConfigView
          organisationInfo={organisationInfo}
          billingProfileCount={billingProfiles?.length}
          destinationsCount={destinations?.length}
          onClickEdit={() => {
            setEditWarningModalModalOpen(true);
          }}
          onClickViewBillingProfiles={() => {
            setBillingProfilesModalOpen(true);
          }}
          onClickViewDestinations={() => {
            setDestinationsModalOpen(true);
          }}
        />
      )}
      <IconLink
        icon={{ justify: "left", name: "chevron-left" }}
        text={t("core:customers:back")}
        appearance="secondary"
        customRouterProps={{
          to: customerLink,
        }}
      />

      <AccountConfigFormModal
        isOpen={accountConfigModalOpen}
        onSubmit={onSubmitAccountConfig}
        isSubmitting={isAccountConfigSubmitting}
        error={formError}
        accountConfigToEdit={accountConfigToEdit}
        onClose={(source) => {
          if (mode === "add" && source !== undefined) {
            navigate(customerLink);
          } else {
            setAccountConfigModalOpen(false);
          }
        }}
        onCancel={() => {
          if (mode === "add") {
            navigate(customerLink);
          } else {
            setAccountConfigModalOpen(false);
          }
        }}
      />

      <SuccessModal
        isOpen={successModalModalOpen}
        srName={t("core:accountConfig:success_create_screen")}
        body={
          <>
            {mode === "add" && (
              <Span display="block">
                {t("core:accountConfig:submitted_succuss", {
                  customerName,
                  productName,
                })}
              </Span>
            )}
            {!isTomiaProvisioned && (
              <Span> {t("core:accountConfig:tomia_check_begin")}</Span>
            )}
          </>
        }
        heading={
          !isTomiaProvisioned
            ? submittedSuccussTitle
            : t("core:accountConfig:update_success")
        }
        confirmText={t("core:accountConfig:okay")}
        onClose={() => {
          navigate(`/customers/${customerId}/product-config/${productId}`);
          setSuccessModalModalOpen(false);
        }}
      />

      <WarningModal
        isOpen={editWarningModalModalOpen}
        srName={t("core:accountConfig:edit_warning_screen")}
        body={
          <Span display="block">
            {t("core:accountConfig:edit_confirmation")}
          </Span>
        }
        heading={t("core:accountConfig:edit_warn_title")}
        confirmText={t("core:accountConfig:proceed")}
        cancelText={t("core:accountConfig:cancel")}
        onCancel={() => {
          setEditWarningModalModalOpen(false);
        }}
        onConfirm={() => {
          setAccountConfigModalOpen(true);
          setEditWarningModalModalOpen(false);
        }}
      />

      <Modal
        srName={t("core:accountConfig:billing_profiles")}
        isOpen={billingProfilesModalOpen}
        size={3}
        animate
        isClosable
        onClose={() => {
          setBillingProfilesModalOpen(false);
        }}
      >
        <BillingProfilesList
          billingProfiles={billingProfiles || []}
          isLoading={isLoadingBillingProfiles}
        />
      </Modal>

      <Modal
        srName={t("core:accountConfig:destinations")}
        isOpen={destinationsModalOpen}
        size={3}
        animate
        isClosable
        onClose={() => {
          setDestinationsModalOpen(false);
        }}
      >
        <DestinationsList
          destinations={destinations || []}
          isLoading={isLoadingDestinations}
        />
      </Modal>
    </>
  );
};

export default AccountConfigurationTab;
